import type { Goal } from '@medplum/fhirtypes';
import React, { useMemo } from 'react';

import { Grid, Icon, Stack, Tag, Text, cn } from '@eluve/components';
import { type FhirPageControls, LoadMoreRow } from '@eluve/fhir-data';
import {
  type GroupedObservations,
  TrendSparkline,
  formatMeasureValue,
} from '@eluve/fhir-observations';
import {
  FhirResourceScope,
  RenderResource,
  buildResolutionMap,
  goalProgress,
} from '@eluve/fhir-renderer';

import {
  type GoalList,
  firstPendingGoal,
  formatGoalTarget,
  goalToken,
  latestTrackedValue,
  pendingReviewCount,
  trackedGoalCount,
  trackedObservationGroup,
  trackingLabel,
} from '../goal-list';
import { isPendingReview } from '../review-markers';

/**
 * The Overview-tab Goals card (ENG-5367): pending AI drafts and in-progress
 * goals first (primary leading), achieved / ended goals below. Rows are
 * rendered by the FHIR renderer (`GoalView` inList); the tracked-observation
 * treatment (sparkline + now/target line), Draft badging, and the review
 * pill are app chrome around renderer primitives, per the renderer's
 * presentational contract.
 */
export type GoalsCardProps = {
  list: GoalList;
  /** The loaded observation window goals resolve tracked series from. */
  grouped: GroupedObservations | null;
  loading: boolean;
  failed: boolean;
  paging: FhirPageControls;
  /** Open the detail drawer for a goal (routing owned by the section). */
  onOpenGoal: (token: string) => void;
};

const CenteredNote: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Grid place="center" className="px-6 py-12">
    <Text size="sm" color="tertiary" weight="regular">
      {children}
    </Text>
  </Grid>
);

const PendingDot: React.FC = () => (
  <span
    aria-hidden
    className="size-1.75 rounded-full border-[1.5px] bg-bg-primary"
    style={{ borderColor: 'var(--color-text-insights)' }}
  />
);

/** Filled status dot leading each row: green met, orange at risk, else neutral. */
const GoalDot: React.FC<{ goal: Goal; pending: boolean }> = ({
  goal,
  pending,
}) => {
  if (pending) return <PendingDot />;
  const progress = goalProgress(goal);
  return (
    <span
      aria-hidden
      className={cn(
        'size-1.75 rounded-full',
        progress?.color === 'positive'
          ? 'bg-text-positive'
          : progress?.color === 'warning'
            ? 'bg-text-warning'
            : 'bg-text-primary',
      )}
    />
  );
};

const TrackedTrend: React.FC<{
  goal: Goal;
  grouped: GroupedObservations | null;
}> = ({ goal, grouped }) => {
  const group = trackedObservationGroup(goal, grouped);
  const label = trackingLabel(goal, group);
  const now = latestTrackedValue(group);
  const target = formatGoalTarget(goal);
  const latestPending = group?.pending[group.pending.length - 1];
  const atRisk = goalProgress(goal)?.color === 'warning';

  if (!label && !target) return null;
  return (
    <>
      {label && (
        <Text size="xs" color="tertiary" weight="regular" className="mt-0.5">
          Tracking {label}
        </Text>
      )}
      {(now ?? target) && (
        <Stack
          direction="horizontal"
          gap={3}
          align="center"
          fullWidth={false}
          className="mt-1"
        >
          {group && group.series.length + group.pending.length > 1 && (
            <TrendSparkline
              className={atRisk ? 'text-text-warning' : 'text-text-positive'}
              points={group.series.map((point) => ({
                value: point.value,
                amended: point.amended,
              }))}
              proposedValue={latestPending?.valueQuantity?.value ?? undefined}
            />
          )}
          <Text
            inline
            size="xs"
            color="tertiary"
            weight="regular"
            className="tabular-nums"
          >
            {now && (
              <>
                now{' '}
                <Text
                  inline
                  size="xs"
                  weight="semibold"
                  className={atRisk ? 'text-text-warning' : undefined}
                >
                  {formatMeasureValue(now.value, group?.unit)}
                </Text>
              </>
            )}
            {now && target && ' · '}
            {target && `target ${target}`}
          </Text>
        </Stack>
      )}
    </>
  );
};

const GoalRows: React.FC<{
  goals: Goal[];
  grouped: GroupedObservations | null;
  onOpenGoal: (token: string) => void;
}> = ({ goals, grouped, onOpenGoal }) => {
  const map = useMemo(() => buildResolutionMap(goals), [goals]);
  return (
    <FhirResourceScope map={map}>
      {goals.map((goal) => {
        const pending = isPendingReview(goal);
        return (
          <button
            key={goalToken(goal)}
            type="button"
            onClick={() => onOpenGoal(goalToken(goal))}
            data-testid="goal-row"
            className="flex w-full items-center gap-3 px-4 text-left not-first:border-t not-first:border-border-primary hover:bg-bg-secondary"
          >
            <GoalDot goal={goal} pending={pending} />
            <div className="min-w-0 flex-1 py-1">
              <RenderResource resource={goal} inList />
              <div className="-mt-1 pb-2">
                <TrackedTrend goal={goal} grouped={grouped} />
              </div>
            </div>
            {pending && (
              <Tag pill subtle size="xs" color="insights" label="Draft" />
            )}
            <Icon
              name="ChevronRight"
              size="xs"
              className="shrink-0 text-text-tertiary"
            />
          </button>
        );
      })}
    </FhirResourceScope>
  );
};

export const GoalsCard: React.FC<GoalsCardProps> = ({
  list,
  grouped,
  loading,
  failed,
  paging,
  onOpenGoal,
}) => {
  const pendingCount = pendingReviewCount(list);
  const firstPending = firstPendingGoal(list);
  const total = trackedGoalCount(list);
  const isEmpty = total === 0;

  return (
    <section
      data-testid="goals-card"
      className="rounded-lg border border-border-primary bg-bg-primary"
    >
      <Stack
        direction="horizontal"
        gap={2}
        align="center"
        justify="between"
        className="h-12 px-4"
      >
        <Text inline size="sm" weight="semibold">
          Goals
        </Text>
        <Stack direction="horizontal" gap={2} align="center" fullWidth={false}>
          {pendingCount > 0 && firstPending && (
            <button
              type="button"
              onClick={() => onOpenGoal(goalToken(firstPending))}
              data-testid="goals-review-pill"
              className="flex items-center gap-1.5 rounded-full border border-border-primary px-2.5 py-0.5 text-xs font-semibold hover:bg-bg-secondary"
            >
              <PendingDot />
              {pendingCount} to review
            </button>
          )}
          {total > 0 && (
            <Text inline size="xs" color="tertiary" weight="regular">
              {total} tracked
            </Text>
          )}
        </Stack>
      </Stack>

      {failed ? (
        <CenteredNote>Couldn't load goals.</CenteredNote>
      ) : loading ? (
        <CenteredNote>Loading goals…</CenteredNote>
      ) : isEmpty ? (
        <CenteredNote>No goals recorded.</CenteredNote>
      ) : (
        <>
          {(list.pending.length > 0 || list.inProgress.length > 0) && (
            <div className="border-t border-border-primary">
              <GoalRows
                goals={list.pending}
                grouped={grouped}
                onOpenGoal={onOpenGoal}
              />
              <GoalRows
                goals={list.inProgress}
                grouped={grouped}
                onOpenGoal={onOpenGoal}
              />
            </div>
          )}
          {list.ended.length > 0 && (
            <div className="border-t border-border-primary">
              <Text
                size="xs"
                color="tertiary"
                weight="medium"
                className="px-4 pt-3 pb-1 tracking-wide uppercase"
              >
                Completed & ended
              </Text>
              <GoalRows
                goals={list.ended}
                grouped={grouped}
                onOpenGoal={onOpenGoal}
              />
            </div>
          )}
          <LoadMoreRow paging={paging} noun="goals" />
        </>
      )}
    </section>
  );
};
